import React, { useRef, useState, useEffect } from 'react';
import './DesignDocs.css';
import DecryptedText from '../blocks/DecryptedText/DecryptedText';
import MetaBalls from '../blocks/MetaBalls/MetaBalls';
import SpotlightCard from '../blocks/SpotlightCard/SpotlightCard';
import ClickSpark from '../blocks/ClickSpark/ClickSpark';
import GooeyNav from '../blocks/GooeyNav/GooeyNav';
import { CSSProperties } from 'react';
import { detectBrowserLanguage, getTranslations, Language } from "../utils/localization";

type DocType = 'colorChronicles' | 'desofPan' | 'witchhunt' | 'vita';

const languageSwitcherStyle: CSSProperties = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000,
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden'
};

const DesignDocs: React.FC = () => {
  // Language state
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const [translations, setTranslations] = useState(getTranslations(language).designDocs);

  // Update translations when language changes
  useEffect(() => {
    setTranslations(getTranslations(language).designDocs);
    // Update HTML lang attribute to reflect language change
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en';
  }, [language]);

  // Toggle language function
  const toggleLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeDoc, setActiveDoc] = useState<DocType>('colorChronicles');
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Back to top visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navItems = translations.navItems.map(item => ({ label: item, href: `#${item.toLowerCase().replace(/\s+/g, '')}` }));

  const handleNavSelect = (index: number) => {
    const docTypes: DocType[] = ['colorChronicles', 'desofPan', 'witchhunt', 'vita'];
    setActiveDoc(docTypes[index]);
  };

  const getPdfPath = (): string => {
    switch(activeDoc) {
      case 'colorChronicles':
        return '/images/projects/designdoc/colorChronicles.jpg';
      case 'desofPan':
        return '/images/projects/designdoc/desofPan.jpg';
      case 'witchhunt':
        return '/images/projects/designdoc/witchhunt.jpg';
      default:
        return '/images/projects/designdoc/colorChronicles.jpg';
    }
  };

  const getVitaImages = (): string[] => {
    return Array.from({ length: 7 }, (_, i) => 
      `/images/projects/designdoc/vita${(i + 1).toString().padStart(2, '0')}.jpg`
    );
  };

  const getProjectDescription = (): string => {
    switch(activeDoc) {
      case 'colorChronicles':
        return translations.projectDescriptions.colorChronicles;
      case 'desofPan':
        return translations.projectDescriptions.desofPan;
      case 'witchhunt':
        return translations.projectDescriptions.witchhunt;
      case 'vita':
        return translations.projectDescriptions.vita;
      default:
        return "";
    }
  };

  const renderDocumentView = () => {
    if (activeDoc === 'vita') {
      return (
        <div className="pdf-container vita-container">
          {getVitaImages().map((src, index) => (
            <img 
              key={`vita-${index}`}
              src={src}
              alt={`Vita design document page ${index + 1}`}
              className="pdf-viewer vita-image"
              width="100%"
            />
          ))}
        </div>
      );
    }
    
    return (
      <div className="pdf-container">
        <img 
          src={getPdfPath()} 
          alt={`${activeDoc} design document`}
          className="pdf-viewer"
          width="100%"
        />
      </div>
    );
  };

  return (
<ClickSpark
                shape="heart"
                sparkColor='#BD2735'
                sparkSize={8}
                sparkRadius={40}
                sparkCount={16}
                duration={500}
              >      <div className="project-page design-docs-page">
        <div className="home-link">
          <div className="metaball-wrapper">
            <MetaBalls 
              color="#266867" 
              enableTransparency={true}
              ballCount={8}
              animationSize={25}
              speed={0.2}
              cursorBallColor="#266867"
            />
          </div>
          <a href="/">Home</a>
        </div>

        {/* Language Switcher */}
        <div style={languageSwitcherStyle}>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => toggleLanguage('en')}
          >
            EN
          </button>
          <button 
            className={`lang-btn ${language === 'zh' ? 'active' : ''}`}
            onClick={() => toggleLanguage('zh')}
          >
            中文
          </button>
        </div>

        <header className="project-header">
          <div className="container">
            <h1>
              <DecryptedText 
                text={translations.title}
                animateOn="view"
                sequential={true}
                maxIterations={4}
                speed={60}
                className="decrypted-title"
                parentClassName="title-container"
                encryptedClassName="encrypted-title"
              />
            </h1>
            <hr />
            {/* <h2>
              <DecryptedText 
                text={translations.subtitle}
                animateOn="view"
                sequential={true}
                maxIterations={1}
                speed={20}
                className="decrypted-description"
                parentClassName="description-container"
                encryptedClassName="encrypted-description"
              />
            </h2> */}
          </div>
        </header>

        <div className="gooey-nav-wrapper">
          <GooeyNav 
            items={navItems}
            initialActiveIndex={navItems.findIndex(item => item.href === `#${activeDoc}`)}
            animationTime={600}
            particleCount={15}
            particleDistances={[90, 10]}
            particleR={100}
            timeVariance={300}
            colors={[1, 2, 3, 1, 2, 3, 1, 4]}
            onItemSelect={(_, index: number) => handleNavSelect(index)}
          />
        </div>

        <main className="project-content">
          <div className="container">
            {/* Project Description Card */}
            <SpotlightCard 
              className="project-section description-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              <div className="project-description">
                <p>{getProjectDescription()}</p>
              </div>
            </SpotlightCard>

            <SpotlightCard 
              className="project-section pdf-section" 
              spotlightColor="rgba(38, 104, 103, 0.15)"
            >
              {renderDocumentView()}
            </SpotlightCard>
          </div>
        </main>

        {/* Back to Top Button */}
        <div 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </div>
      </div>
    </ClickSpark>
  );
};

export default DesignDocs; 